import { segment } from '@actions/analytics';
import { addCourseToPlanner } from '@actions/courses';
import Drawer from '@base/Drawer/Drawer';
import { evtNameCategoryMap, programmeTypes } from '@constants/index';
import {
  ADD_TO_PLANNER_CUSTOMISE_CLICKED,
  COURSE_KEYS,
  TEST_SERIES_KEYS
} from '@constants/segment';
import styled from '@emotion/styled';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { Spacings } from '@styles/index';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { AnyArgVoidReturnFunction } from 'types/utils';
import CustomizeAddDrawerContent from './CustomizedAddDrawer';
import SimpleAddDrawerContent from './SimpleAddDrawer';

const Wrapper = styled.div`
  grid-column: 1/10;
  padding-bottom: ${Spacings.SPACING_6B};
`;

interface ILessonList {
  loaded: boolean;
  total: number;
  results: any[];
}

interface IAddDrawerProps {
  lessonList: ILessonList;
  drawerState: any;
  goalUid: string;
  courseUid: string;
  courseType: number;
  plusCourse: any;
  plannerSchedule: any;
  closeDrawerHandler: AnyArgVoidReturnFunction;
  isEditFlow?: boolean;
}

const AddCourseToPlannerDrawer: React.FC<IAddDrawerProps> = ({
  isEditFlow = false,
  drawerState,
  closeDrawerHandler,
  courseUid,
  plannerSchedule,
  plusCourse,
  courseType,
  goalUid,
  lessonList
}) => {
  const goalSegmentData = useGoalEventProps({ goalUID: goalUid });
  const segmentData = useAllTypesOfCourseEventProps({ course: plusCourse });
  const hasWatchedLessons = React.useMemo(
    () => !!lessonList?.results?.find((lesson) => lesson.hasWatched),
    [lessonList]
  );
  const lessonCount = React.useMemo(
    () => lessonList?.results?.length ?? 0,
    [lessonList]
  );
  const eventKeys =
    courseType === programmeTypes.TESTSERIES ? TEST_SERIES_KEYS : COURSE_KEYS;

  const dispatch = useDispatch();
  const [customiseAdd, setCustomiseAdd] = React.useState(false);
  const handleClose = () => {
    setCustomiseAdd(false);
    closeDrawerHandler();
  };

  const onClickCustomise = () => {
    setCustomiseAdd(true);
    segment.track(
      ADD_TO_PLANNER_CUSTOMISE_CLICKED,
      { ...goalSegmentData, ...segmentData },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };

  const onClickConfirm = ({
    selectedChoice,
    include_completed_lessons = true,
    days = [0, 1, 2, 3, 4, 5, 6],
    start_date = null
  }) => {
    dispatch(
      addCourseToPlanner(
        courseUid,
        selectedChoice,
        include_completed_lessons,
        days,
        start_date
      )
    );
    handleClose();
  };

  const addDrawerContent = customiseAdd ? (
    <CustomizeAddDrawerContent
      isEditFlow={isEditFlow}
      goalUid={goalUid}
      courseType={courseType}
      plusCourse={plusCourse}
      plannerSchedule={plannerSchedule}
      onClickConfirm={onClickConfirm}
      lessonCount={lessonCount}
      hasWatchedLessons={hasWatchedLessons}
    />
  ) : (
    <SimpleAddDrawerContent
      isEditFlow={isEditFlow}
      goalUid={goalUid}
      courseType={courseType}
      plusCourse={plusCourse}
      plannerSchedule={plannerSchedule}
      onClickConfirm={onClickConfirm}
      onClickCustomise={onClickCustomise}
    />
  );

  return (
    <Drawer
      drawerState={drawerState}
      onClose={handleClose}
      backgroundColor="var(--color-base-0)"
      className="AddToPlanner-DrawerPaper"
    >
      <Wrapper>{addDrawerContent}</Wrapper>
    </Drawer>
  );
};

export default AddCourseToPlannerDrawer;
